import styled from '@emotion/styled';
import * as React from 'react';
import { useState } from 'react';
import { connect } from 'react-redux';
import { signUp } from '../../actions/newsletterAction';
import { CTA } from '../../components/CTA';
import { config } from '../../config';
import * as Colors from '../../consts/variables';
import * as Cookie from '../../helpers/cookie';
import { eventTracker } from '../../helpers/EventTracker/EventTracker';
import { isMobile as isBrowserMobile } from '../../helpers/responsiveHelpers/responsiveHelpers';
import { TestSelector } from '../../helpers/TestSelector';
import { __ } from '../../helpers/TranslationService';
import { validateEmail } from '../../helpers/validation';
import useWindowSize, { IViewPortSize } from '../../hooks/useViewport';
import { IPageDataReducer } from '../../reducers/pageData';
import { ArrowDownIcon } from '../Icons/ArrowDownIcon';
import { ArrowRightIcon } from '../Icons/ArrowRightIcon';
import { AttentionIcon } from '../Icons/AttentionIcon';
import { CheckMarkIcon } from '../Icons/CheckMarkIcon';
import { CrossIcon } from '../Icons/CrossIcon';
import { NewsletterIcon } from '../Icons/NewsletterIcon';
import { LoadingIndicatorControlled } from '../LoadingIndicator/LoadingIndicator';

const style: Record<string, React.CSSProperties> = {
    icon: {
        fill: Colors.white,
        marginRight: '18px',
        flex: '0 0 auto',
    },

    CTAWrapper: {
        width: '40%',
        minWidth: 100,
        margin: '0 0 0 13px',
        fontSize: 17,
        fontWeight: 'bold',
        padding: 10,
        lineHeight: '20px',
        float: 'right',
    },
    arrow: {
        fill: Colors.white,
        height: 17,
        width: 17,
    },
    attentionIcon: {
        fill: Colors.white,
        marginRight: 5,
    },
    error: {
        color: Colors.white,
        marginTop: 5,
        fontSize: 15,
        display: 'flex',
    },
    loadingIndicator: {
        position: 'absolute',
        textAlign: 'center',
        fontSize: '3px',
        margin: '18px 18px 18px auto',
    },

    checkMarkIcon: {
        flex: '0 0 auto',
        marginRight: 10,
        fill: Colors.white,
        alignSelf: 'flex-start',
    },

    rightArrow: {
        fill: Colors.white,
        height: 17,
        width: 17,
        marginLeft: 10,
        flex: '0 0 auto',
    },
    pointer: {
        cursor: 'pointer',
    },
    GDPRtext: {
        color: Colors.white,
        fontSize: 11,
    },
    header: {
        alignItems: 'center',
        display: 'flex',
    },
    emailInput: {
        lineHeight: '20px',
        fontSize: '15px',
        padding: '9px',
        outline: 'none',
        border: '1px solid transparent',
        width: '100%',
    },
};

const footerDesktopStyle: Record<string, React.CSSProperties> = {
    emailInput: {
        width: '450px',
    },
};
const searchStyle: Record<string, React.CSSProperties> = {
    emailInput: {
        textAlign: 'center',
    },
};

const newsLetterOnSearchpageStyles: Record<string, React.CSSProperties> = {
    CTAWrapper: {
        float: 'none',
        margin: '10px 0',
        width: '100%',
        order: 1,
    },
    registerText: {
        fontSize: '15px',
    },
};

export const NewsletterWrapper = styled.div`
    position: relative;
    background: rgb(0, 101, 152);
    border: 0;
    padding: 13px 15px 30px 15px;
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.27);
    @media screen and (min-width: 1280px) {
        margin-left: -15px;
        margin-right: -15px;
    }
`;
export const OverlayWrapper = styled.div`
    display: none;
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    .newsLetter-responsive & {
        @media only screen and (min-width: 1280px) {
            display: block;
            background-color: rgba(11, 78, 128, 0.7);
        }
    }
`;

interface StateProps {
    pageData: IPageDataReducer;
}

interface DispatchProps {
    signUp: Function;
}

interface OwnProps {
    location: string;
    testSelectorName?: string;
    setHideNewsletter: (hide: boolean) => void;
}

type IProps = StateProps & DispatchProps & OwnProps;

const shouldShowNewsletterForm = (portalId: string) => {
    const notAllowedMarkets: string[] = ['dreamlines.it', 'dreamlines.fr', 'dreamlines.com.br'];

    return (
        Cookie.readCookie('isSignedUpForNewsletter') !== 'true' &&
        !notAllowedMarkets.includes(portalId)
    );
};

const NewsletterClass = ({ location, pageData, signUp }: IProps) => {
    const { width }: IViewPortSize = useWindowSize();
    const isMobile = isBrowserMobile(width);

    const [showNewsletter, setShowNewsletter] = useState(true);
    const [state, setState] = useState({
        expanded: false,
        email: '',
        isLoading: false,
        isSignUpSuccessful: false,
        isError: false,
    });

    React.useEffect(() => {
        setShowNewsletter(shouldShowNewsletterForm(pageData.brandConfig.portalId));
    }, []);

    const showFooterStyleForm = location === 'footer' || (location === 'search' && !isMobile);
    const searchNewsletterStyles = showFooterStyleForm ? {} : newsLetterOnSearchpageStyles;
    const GDRPTextStyle = showFooterStyleForm
        ? {
              ...style.GDPRtext,
              marginTop: 10,
          }
        : style.GDPRtext;

    const containerClass =
        'newsLetter--wrapper ' +
        (location !== 'footer' ? 'searchNewsletter' : 'newsLetter-responsive');

    const newsletterStyle =
        width >= 768 && location === 'footer' ? footerDesktopStyle : searchStyle;

    const onEmailChanged = (e) => {
        setState({
            ...state,
            email: e.target.value,
            isError: state.isError ? !validateEmail(e.target.value) : false,
        });
    };

    const onExpandHandler = () => {
        setState({ ...state, expanded: !state.expanded });
    };

    const onCloseHandler = () => {
        setShowNewsletter(false);
    };

    const handleSubmitClick = () => {
        if (!validateEmail(state.email)) {
            setState({ ...state, isLoading: false, isError: true });
            return;
        }

        setState({ ...state, isLoading: true });
        signUp(state.email).then(
            () => {
                eventTracker.track({
                    category: 'newsletter',
                    action: 'footer',
                    label: 'cruisepage',
                });
                setState({ ...state, isLoading: false, isSignUpSuccessful: true });
            },
            () => {
                setState({ ...state, isLoading: false, isSignUpSuccessful: false });
            },
        );
    };

    if (!showNewsletter) {
        return null;
    }

    return (
        <NewsletterWrapper
            style={{
                backgroundImage: `url(${config.images}/common/faq_contact_background.jpg)`,
            }}
            className={containerClass}
        >
            <OverlayWrapper />
            {!state.isSignUpSuccessful && (
                <div>
                    <div
                        data-test-id="newsletterMobileOffer"
                        className="newsLetter--header"
                        style={{ ...style.pointer, ...style.header }}
                        onClick={location === 'footer' ? null : onExpandHandler}
                    >
                        <NewsletterIcon width={30} height={30} style={{ ...style.icon }} />
                        <p style={{ ...searchNewsletterStyles.registerText }}>
                            {' '}
                            {__(
                                'Register for our newsletter and get 30€ Onboardcredit',
                                'dreamlines',
                            )}
                        </p>
                        {location !== 'footer' ? (
                            state.expanded ? (
                                <ArrowDownIcon style={style.rightArrow} />
                            ) : (
                                <ArrowRightIcon style={style.rightArrow} />
                            )
                        ) : null}
                    </div>
                    {(location === 'footer' || state.expanded) && (
                        <>
                            <div
                                className={`${
                                    !showFooterStyleForm
                                        ? 'searchNewsletter--container'
                                        : 'newsLetter--container'
                                }`}
                            >
                                <CTA
                                    wrapperStyle={{
                                        ...style.CTAWrapper,
                                        ...searchNewsletterStyles.CTAWrapper,
                                    }}
                                    arrowStyle={style.arrow}
                                    onClick={handleSubmitClick}
                                    testSelectorName="newsletterSubmitButton"
                                >
                                    <span className="newsLetter--btnText">
                                        {__('Send', 'dreamlines')}
                                    </span>
                                </CTA>
                                <div className="newsLetter--emailWrapper">
                                    <LoadingIndicatorControlled
                                        isLoading={state.isLoading}
                                        style={style.loadingIndicator}
                                    />
                                    <input
                                        type="email"
                                        placeholder={__('Your E-Mail', 'dreamlines')}
                                        value={state.email}
                                        onChange={onEmailChanged}
                                        style={{
                                            ...style.emailInput,
                                            ...newsletterStyle.emailInput,
                                        }}
                                        className={`newsLetter--email-input ${
                                            state.isError ? 'is-error' : ''
                                        }`}
                                        {...TestSelector.getSelector('newsletterEmail')}
                                    />
                                </div>
                                {state.isError && (
                                    <div style={style.error}>
                                        <AttentionIcon
                                            width={16}
                                            height={16}
                                            style={style.attentionIcon}
                                        />
                                        <span>{__('incorrect email', 'dreamlines')}</span>
                                    </div>
                                )}
                            </div>
                            <div
                                style={GDRPTextStyle}
                                className="newsLetter--html-link"
                                dangerouslySetInnerHTML={{
                                    __html: __('newsletter related GDPR text', 'dreamlines'),
                                }}
                            />
                        </>
                    )}
                </div>
            )}

            {state.isSignUpSuccessful && (
                <div className="newsLetter--thankYouWrapper">
                    <CheckMarkIcon style={style.checkMarkIcon} />
                    <div
                        className="newsLetter--confirmEmailWrapper"
                        {...TestSelector.getSelector('newsletterMessage')}
                    >
                        <div>
                            {__('Thank you! Your registration was successful!', 'dreamlines')}
                        </div>
                        <div className="newsLetter--confirmEmailWrapper-email-text">
                            {__(
                                'you are just one step away from getting all top offers: Please go to your E-Mail provider and verify your E-Mail address.',
                                'dreamlines',
                            )}
                        </div>
                    </div>
                    <div
                        className="newsLetter--thankYouWrapper-cross-icon"
                        onClick={onCloseHandler}
                    >
                        <CrossIcon style={style.pointer} />
                    </div>
                </div>
            )}
        </NewsletterWrapper>
    );
};

const mapStateToProps = (state: StateProps) => ({
    pageData: state.pageData,
});
const mapDispatchToProps = { signUp };

export const Newsletter = connect<StateProps, DispatchProps, any>(
    mapStateToProps,
    mapDispatchToProps,
)(NewsletterClass);
